import { Fragment, useState } from 'react'
import MenuItem from './MenuItem'
import './MenuBar.scss'
import EditorImageModal from '../../modals/EditorImageModal.tsx'
import EditorVideoModal, { VideoType } from '../../modals/EditorVideoModal'
import EditorLinkModal from '../../modals/EditorLinkModal'
import EditorLeadFormModal from '../../modals/EditorLeadFormModal'
import { cld } from '../../../utils/cloudinary.ts'


const getLinkTitle = (editor) => {
  const {selection} = editor.state
  
  if (editor.isActive('link')) {
    return 'Edit link'
  }

  if (selection.empty) {
    return 'Select text to add a link'
  }

  return 'Add link'
}

const getLinkProps = (editor) => {
  const {selection} = editor.state
  const isActiveLink = editor.isActive('link')

  return {
    disabled: !isActiveLink && selection.empty,
    title: getLinkTitle(editor),
    isActive: () => isActiveLink,
  }
}
export default function MenuBar({ editor, allowSponsoredLinks }) {
  const [showAddImageModal, setShowAddImageModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showLeadFormModal, setShowLeadFormModal] = useState(false)

  const isImageSelected = editor.isActive('image')
  const imageAlignment = isImageSelected ? editor.getAttributes('image').alignment : null

  const isYoutubeSelected = editor.isActive('youtube')
  const isContimoVideoSelected = editor.isActive('video')
  const isVideoSelected = isYoutubeSelected || isContimoVideoSelected

  const items = [
    {
      icon: 'bold',
      title: 'Bold',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
    },
    {
      icon: 'italic',
      title: 'Italic',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
    },
    {
      icon: 'strikethrough',
      title: 'Strike',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
    },

    {
      icon: 'mark-pen-line',
      title: 'Highlight',
      action: () => editor.chain().focus().toggleHighlight().run(),
      isActive: () => editor.isActive('highlight'),
    },
    {
      type: 'divider',
    },
    {
      icon: 'align-left',
      title: 'Align left',
      action: () => {
        if (isImageSelected) {
          editor.chain().focus().updateAttributes("image", { alignment: "left" }).run()
        } else {
          editor.chain().focus().setTextAlign('left').run()
        }
      },
      isActive: () => isImageSelected
        ? imageAlignment === 'left'
        : editor.isActive({ textAlign: 'left' }),
    },
    {
      icon: 'align-center',
      title: 'Align center',
      action: () => {
        if (isImageSelected) {
          editor.chain().focus().updateAttributes("image", { alignment: "center" }).run()
        } else {
          editor.chain().focus().setTextAlign('center').run()
        }
      },
      isActive: () => isImageSelected
        ? imageAlignment === 'center'
        : editor.isActive({ textAlign: 'center' }),
    },
    {
      icon: 'align-right',
      title: 'Align right',
      action: () => {
        if (isImageSelected) {
          editor.chain().focus().updateAttributes("image", { alignment: "right" }).run()
        } else {
          editor.chain().focus().setTextAlign('right').run()
        }
      },
      isActive: () => isImageSelected
        ? imageAlignment === 'right'
        : editor.isActive({ textAlign: 'right' }),
    },
    {
      type: 'divider',
    },
    ...[1, 2, 3, 4, 5, 6].map((level) => ({
      icon: `h-${level}`,
      title: `Heading ${level}`,
      action: () => editor.chain().focus().toggleHeading({ level }).run(),
      isActive: () => editor.isActive('heading', { level }),
    })),
    {
      icon: 'paragraph',
      title: 'Paragraph',
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive('paragraph'),
    },
    {
      icon: 'list-unordered',
      title: 'Bullet List',
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      icon: 'list-ordered',
      title: 'Ordered List',
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
    },
    {
      type: 'divider',
    },
    {
      icon: 'link',
      action: () => setShowLinkModal(true),
      ...getLinkProps(editor),
    },
    {
      icon: 'double-quotes-l',
      title: 'Blockquote',
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive('blockquote'),
    },
    {
      icon: 'separator',
      title: 'Horizontal Rule',
      action: () => editor.chain().focus().setHorizontalRule().run(),
    },
    {
      icon: isImageSelected ? 'image-edit-line' : 'image-add-line',
      title: isImageSelected ? 'Edit image' : 'Insert image',
      action: () => setShowAddImageModal(true),
    },
    {
      icon: isVideoSelected ? 'live-line' : 'video-add-line',
      title: isVideoSelected ? 'Edit video' : 'Insert video',
      action: () => setShowVideoModal(true),
      isActive: () => isVideoSelected,
    },
    {
      icon: 'information-line',
      title: 'Insert Factbox',
      action: () => editor.chain().focus().toggleFactBox().run(),
      isActive: () => editor.isActive('factbox'),
    },
    {
      icon: 'code-box-line',
      title: 'Insert code block',
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive('code-block'),
    },
    // {
    //   icon: 'mail-line',
    //   title: 'Lead Form',
    //   action: () => setShowLeadFormModal(true),
    //   isActive: () => editor.isActive('leadform'),
    // },
    {
      icon: 'money-dollar-box-line',
      title: 'Sponsored links',
      action: () => editor.chain().focus().insertSponsoredLinks().run(),
      isActive: () => editor.isActive('sponsoredLinks'),
      visible: allowSponsoredLinks
    },
    {
      type: 'divider',
    },
    {
      icon: 'text-wrap',
      title: 'Hard Break',
      action: () => editor.chain().focus().setHardBreak().run(),
    },
    {
      icon: 'format-clear',
      title: 'Clear Format',
      action: () => editor.chain().focus().clearNodes().unsetAllMarks()
        .run(),
    },
    {
      type: 'divider',
    },
    {
      icon: 'arrow-go-back-line',
      title: 'Undo',
      action: () => editor.chain().focus().undo().run(),
    },
    {
      icon: 'arrow-go-forward-line',
      title: 'Redo',
      action: () => editor.chain().focus().redo().run(),
    },
  ]

  return (
    <div className="editor__header sticky top-0 z-10 bg-white rounded-tl-md rounded-tr-md">
      {items.map((item, index) => (
        <Fragment key={index}>
          {item.type === 'divider' ? <div className="divider" /> : <MenuItem {...item} />}
        </Fragment>
      ))}
      <EditorImageModal
        open={showAddImageModal}
        {...isImageSelected ? {
          defaultValues: {
            ...editor.getAttributes('image'),
          }
        } : {}}
        onOk={(values) => {
          const src = cld.image(values.publicId).toURL()
          const options = { ...values, src }

          if (isImageSelected) {
            editor.chain().focus().updateImage(options).run()
          } else {
            editor.chain().focus().insertImage(options).run()
          }
        }}
        onCancel={() => setShowAddImageModal(false)}
      />

      <EditorVideoModal
        open={showVideoModal}
        {...isVideoSelected ? {
          defaultValues: {
            type: isYoutubeSelected ? VideoType.YOUTUBE : VideoType.CONTIMO,
            ...(isYoutubeSelected
              ? { src: editor.getAttributes('youtube').src }
              : editor.getAttributes('video')
            ),
          }
        } : {}}
        onOk={({type, ...values}) => {
          if (type === VideoType.YOUTUBE) {
            if (isYoutubeSelected) {
              editor.chain().focus().updateAttributes('youtube', values).run()
            } else {
              editor.chain().focus().setYoutubeVideo(values).run()
            }
          } else {
            if (isContimoVideoSelected) {
              editor.chain().focus().updateAttributes('video', values).run()
            } else {
              editor.chain().focus().insertVideo(values).run()
            }
          }
          setShowVideoModal(false)
        }}
        onCancel={() => setShowVideoModal(false)}
      />

      <EditorLinkModal
        open={showLinkModal}
        editor={editor}
        onOk={(values) => editor.chain().focus().extendMarkRange('link').setLink(values).run()}
        onCancel={() => setShowLinkModal(false)}
      />
      <EditorLeadFormModal
        open={showLeadFormModal}
        {...editor.isActive('leadform') ? {
          defaultValues: {
            ...editor.getAttributes('leadform'),
          }
        } : {}}
        onCancel={() => setShowLeadFormModal(false)}
        onOk={(values) => {
          console.log(values)
          editor.chain().focus().ínsertLeadForm(values).run()
        }}
      />

    </div>
  )
}
